import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Modal } from '@/components/common/modal/Modal';
import { Button } from '@/components/admin/button/Button';
import { HintBox } from '@/components/admin/hint-box/HintBox';
import { ConfirmationModal } from '@/components/admin/confirmation-modal/ConfirmationModal';
import { InputWithCharacterLimitGroup } from '@/components/admin/input-groups/input-with-character-limit-group/InputWithCharacterLimitGroup';
import { SingleSelectInputGroup } from '@/components/admin/input-groups/single-select-input-group/SingleSelectInputGroup';
import { ProgramCategory, ProgramCategoryCreateUpdate } from '@/types/admin/programs';
import { PROGRAM_CATEGORY_VALIDATION_FUNCTIONS } from '@/validation/admin/program-category-schema/program-category-schema';
import { PROGRAM_CATEGORY_TEXT, PROGRAM_CATEGORY_VALIDATION } from '@/const/admin/programs';
import { ProgramsCategoriesApi } from '@/services/api/admin/programs/programs-api';
import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import './ProgramCategoryModal.scss';
import { useAdminClient } from '@/hooks/admin/use-admin-client/useAdminClient';

interface ProgramCategoryFormValues {
    name: string;
}

interface FormErrorState {
    name?: string;
}

interface BaseProps {
    isOpen: boolean;
    onClose: () => void;
    categories: ProgramCategory[];
}

interface AddModalProps extends BaseProps {
    mode: 'add';
    onAddCategory: (category: ProgramCategory) => void;
}

interface EditModalProps extends BaseProps {
    mode: 'edit';
    onEditCategory: (category: ProgramCategory) => void;
}

export type ProgramCategoryModalProps = AddModalProps | EditModalProps;

const validateForm = (formState: ProgramCategoryFormValues): FormErrorState => {
    return {
        name: PROGRAM_CATEGORY_VALIDATION_FUNCTIONS.validateName(formState.name),
    };
};

const hasErrors = (errors: FormErrorState): boolean => {
    return Object.values(errors).some((error) => error !== undefined);
};

export const ProgramCategoryModal = (props: ProgramCategoryModalProps) => {
    const { isOpen, onClose, categories, mode } = props;

    const defaultFormState = useMemo<ProgramCategoryFormValues>(
        () => ({
            name: '',
        }),
        [],
    );

    const [formState, setFormState] = useState<ProgramCategoryFormValues>(defaultFormState);
    const [errors, setErrors] = useState<FormErrorState>({});
    const client = useAdminClient();
    const [initialFormState, setInitialFormState] = useState<ProgramCategoryFormValues>(defaultFormState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
    const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
    const [error, setError] = useState('');

    const selectedCategoryRef = useRef<null | ProgramCategory>(null);

    const reset = useCallback(
        (data?: ProgramCategoryFormValues) => {
            const newState = data || defaultFormState;
            setFormState(newState);
            setInitialFormState(newState);
            setErrors({});
        },
        [defaultFormState],
    );

    const isDirty = JSON.stringify(formState) !== JSON.stringify(initialFormState);

    const isDuplicateName = categories.some((category) => {
        if (mode === 'edit') {
            return (
                category.id !== selectedCategoryRef.current?.id &&
                category.name.trim().toLowerCase() === formState.name.trim().toLowerCase()
            );
        }
        return category.name.trim().toLowerCase() === formState.name.trim().toLowerCase();
    });

    // Name handler
    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormState((prev) => ({ ...prev, name: value }));
    }, []);

    const handleNameBlur = useCallback(() => {
        const error = PROGRAM_CATEGORY_VALIDATION_FUNCTIONS.validateName(formState.name);
        setErrors((prev) => ({ ...prev, name: error }));
    }, [formState.name]);

    const onSubmit = useCallback(
        async (e?: React.FormEvent) => {
            if (e) {
                e.preventDefault();
            }

            if (isSubmitting || isDuplicateName) return;
            if (mode === 'edit' && !selectedCategoryRef.current) return;

            setIsSubmitting(true);
            setError('');
            setShowSaveConfirmModal(false);

            try {
                const categoryData: ProgramCategoryCreateUpdate = {
                    id: mode === 'edit' ? selectedCategoryRef.current!.id : null,
                    name: formState.name.trim(),
                };

                if (mode === 'add') {
                    const newCategory = await ProgramsCategoriesApi.addProgramCategory(categoryData, client);
                    props.onAddCategory(newCategory);
                } else {
                    const updatedCategory = await ProgramsCategoriesApi.editProgramCategory(categoryData, client);
                    props.onEditCategory(updatedCategory);
                }

                onClose();
            } catch {
                const errorMessage =
                    mode === 'add'
                        ? COMMON_TEXT_ADMIN.CATEGORIES.FORM.MESSAGE.FAIL_TO_CREATE_CATEGORY
                        : COMMON_TEXT_ADMIN.CATEGORIES.FORM.MESSAGE.FAIL_TO_UPDATE_CATEGORY;
                setError(errorMessage);
            } finally {
                setIsSubmitting(false);
            }
        },
        [formState, isSubmitting, isDuplicateName, mode, props, onClose, client],
    );

    const handleSubmitClick = useCallback(() => {
        if (isSubmitting || isDuplicateName) return;
        if (mode === 'edit' && !selectedCategoryRef.current) return;

        const formErrors = validateForm(formState);
        setErrors(formErrors);

        if (hasErrors(formErrors)) {
            return;
        }

        if (mode === 'edit') {
            setShowSaveConfirmModal(true);
        } else {
            onSubmit();
        }
    }, [onSubmit, formState, isSubmitting, isDuplicateName, mode]);

    const handleClose = useCallback(() => {
        if (isSubmitting) return;

        if (isDirty) {
            setShowCloseConfirmModal(true);
            return;
        }

        onClose();
    }, [isSubmitting, isDirty, onClose]);

    const handleConfirmClose = useCallback(() => {
        setShowCloseConfirmModal(false);
        onClose();
    }, [onClose]);

    const handleCategoryChange = useCallback(
        (category: ProgramCategory) => {
            const selected = categories.find((cat) => cat.id === category.id);
            if (selected) {
                selectedCategoryRef.current = selected;
                reset({ name: selected.name });
            } else {
                selectedCategoryRef.current = null;
                reset({ name: '' });
            }
        },
        [categories, reset],
    );

    // Reset form on open
    useEffect(() => {
        if (!isOpen) return;

        if (mode === 'edit') {
            if (categories.length > 0) {
                const firstCategory = categories[0];
                selectedCategoryRef.current = firstCategory;
                reset({ name: firstCategory.name });
            } else {
                selectedCategoryRef.current = null;
                reset({ name: '' });
            }
        } else {
            reset({ name: '' });
        }
        setError('');
    }, [isOpen, categories, reset, mode]);

    const isSubmitDisabled = () => {
        const currentValidationError = PROGRAM_CATEGORY_VALIDATION_FUNCTIONS.validateName(formState.name);
        const hasValidationErrors = currentValidationError !== undefined || isDuplicateName;
        const hasEmptyName = !formState.name.trim();

        if (mode === 'edit') {
            const hasNoSelectedCategory = !selectedCategoryRef.current;
            const nameNotChanged =
                !!selectedCategoryRef.current && formState.name.trim() === selectedCategoryRef.current.name.trim();

            return isSubmitting || hasValidationErrors || hasEmptyName || hasNoSelectedCategory || nameNotChanged;
        }

        return isSubmitting || hasValidationErrors || hasEmptyName;
    };

    const getTitle = () => {
        return mode === 'add'
            ? COMMON_TEXT_ADMIN.CATEGORIES.FORM.TITLE.ADD_CATEGORY
            : COMMON_TEXT_ADMIN.CATEGORIES.FORM.TITLE.EDIT_CATEGORY;
    };

    const getFormId = () => {
        return mode === 'add' ? 'add-program-category-form' : 'edit-program-category-form';
    };

    const getFieldId = (field: string) => {
        return mode === 'add' ? `add-category-${field}` : `edit-category-${field}`;
    };

    const handleSaveConfirmClose = useCallback(() => {
        setShowSaveConfirmModal(false);
    }, []);

    const handleSaveConfirmCancel = useCallback(() => {
        setShowSaveConfirmModal(false);
    }, []);

    const handleCloseConfirmClose = useCallback(() => {
        setShowCloseConfirmModal(false);
    }, []);

    const handleCloseConfirmCancel = useCallback(() => {
        setShowCloseConfirmModal(false);
    }, []);

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose}>
                <Modal.Title>{getTitle()}</Modal.Title>
                <Modal.Content>
                    <form
                        onSubmit={(e) => e.preventDefault()}
                        className="program-form-main hide-scrollbar"
                        id={getFormId()}
                    >
                        {mode === 'edit' && (
                            <SingleSelectInputGroup
                                id={getFieldId('select')}
                                label={PROGRAM_CATEGORY_TEXT.FORM.LABEL.CATEGORY}
                                isRequired={true}
                                options={categories}
                                getOptionId={(c) => c.id}
                                getOptionName={(c) => c.name}
                                disabled={isSubmitting}
                                onChange={handleCategoryChange}
                                placeholder={''}
                                value={selectedCategoryRef.current || undefined}
                            />
                        )}

                        <InputWithCharacterLimitGroup
                            isRequired={true}
                            label={
                                mode === 'add'
                                    ? PROGRAM_CATEGORY_TEXT.FORM.LABEL.NAME
                                    : PROGRAM_CATEGORY_TEXT.FORM.LABEL.EDIT_NAME
                            }
                            error={errors.name}
                            value={formState.name}
                            onChange={handleNameChange}
                            onBlur={handleNameBlur}
                            name={'name'}
                            type={'text'}
                            id={getFieldId('name')}
                            maxLength={PROGRAM_CATEGORY_VALIDATION.name.max}
                            disabled={isSubmitting}
                            showCounterBelow
                        />

                        {isDuplicateName && (
                            <HintBox
                                title={PROGRAM_CATEGORY_VALIDATION.name.getCategoryWithThisNameAlreadyExistsError()}
                            />
                        )}

                        {error && <div className="program-category-modal-error-container">{error}</div>}
                    </form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type="button"
                        onClick={handleSubmitClick}
                        buttonStyle="primary"
                        className="w-100"
                        disabled={isSubmitDisabled()}
                    >
                        {COMMON_TEXT_ADMIN.BUTTON.SAVE}
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* Save confirmation */}
            <ConfirmationModal
                isOpen={showSaveConfirmModal}
                title={COMMON_TEXT_ADMIN.QUESTION.SAVE_CHANGES}
                onClose={handleSaveConfirmClose}
                onCancel={handleSaveConfirmCancel}
                onConfirm={onSubmit}
            />

            {/* CLose confirmation */}
            <ConfirmationModal
                isOpen={showCloseConfirmModal}
                title={COMMON_TEXT_ADMIN.QUESTION.CHANGES_WILL_BE_LOST_WISH_TO_CONTINUE}
                onClose={handleCloseConfirmClose}
                onCancel={handleCloseConfirmCancel}
                onConfirm={handleConfirmClose}
            />
        </>
    );
};
